/**
 * @fileoverview Locale loading utilities for Arad modules.
 *
 * STATUS: PREPARED - Not yet in use
 * This file is ready for the address-extension module but will be enabled
 * when the backend implementation is completed and the module is activated.
 *
 * Similar to the core @/modules/utils.ts but scoped to client-app/arad/modules/ path.
 */

import { useLanguages } from "@/core/composables/useLanguages";
import { FALLBACK_LOCALE } from "@/core/constants";
import { Logger } from "@/core/utilities";
import type { I18n } from "@/i18n";
import type { LocaleMessageValue } from "vue-i18n";

/**
 * Load localization messages for an Arad module.
 *
 * @param i18n - The i18n instance
 * @param moduleName - The module folder name (e.g., 'address-extension')
 */
export async function loadModuleLocale(i18n: I18n, moduleName: string): Promise<void> {
  const { currentLanguage, mergeLocalesMessages } = useLanguages();
  const locale = currentLanguage.value?.twoLetterLanguageName || FALLBACK_LOCALE;

  try {
    const [moduleFallbackMessages, moduleMessages] = await Promise.all<LocaleMessageValue[]>([
      locale !== FALLBACK_LOCALE
        ? import(`./${moduleName}/locales/${FALLBACK_LOCALE}.json`).catch((error) => {
            Logger.error(`Fallback locale: ${FALLBACK_LOCALE} for the arad module ${moduleName} not found`, error);
            return {};
          })
        : Promise.resolve({}),
      import(`./${moduleName}/locales/${locale}.json`).catch((error) => {
        Logger.error(`Locale: ${locale} for the arad module ${moduleName} not found`, error);
        return {};
      }),
    ]);

    mergeLocalesMessages(i18n, locale, moduleMessages);

    if (locale !== FALLBACK_LOCALE) {
      mergeLocalesMessages(i18n, FALLBACK_LOCALE, moduleFallbackMessages);
    }
  } catch (error) {
    Logger.error(`Error loading the ${moduleName} arad module locale: "${locale}"`, error);
  }
}
